import { useEffect, useMemo, useState } from "react";
import { api } from "@/api/client";
import { READ_ONLY_TITLE, useReadOnly, useStore } from "@/state/store";
import { Confetti } from "./Confetti";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { detectVoteTie } from "./operator-helpers";
import type { VoteTally } from "@offsite/shared";

export function WinnerRevealOverlay() {
  const { state, dispatch } = useStore();
  const readOnly = useReadOnly();
  const [tallies, setTallies] = useState<VoteTally[]>([]);
  const [numTeams, setNumTeams] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  // Vom Moderator bei Gleichstand manuell gewählter Sieger (eingefroren via API).
  const [chosenId, setChosenId] = useState<string | null>(null);

  // Poll topics + tallies every 4s. Race-fix: phase can flip to
  // winner-reveal while the last vote is still in flight, so we re-read
  // periodically until the operator clicks "verteilen".
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [t, v] = await Promise.all([
          api.topics.list(state.auth.token),
          api.votes.tallies(state.auth.token),
        ]);
        if (cancelled) return;
        dispatch({ type: "topics/set", topics: t.topics });
        setTallies(v.tallies);
      } catch {
        /* noop */
      }
    };
    load();
    const id = setInterval(load, 4000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [state.auth.token, dispatch]);

  const totalVotes = tallies.reduce((a, b) => a + b.count, 0);

  // Default # of teams to a sensible value once we know the vote count.
  // Solo-test (1 voter) → 1 team; otherwise default to min(totalVotes, 4).
  useEffect(() => {
    if (numTeams !== null) return;
    if (totalVotes === 0) return;
    setNumTeams(Math.max(1, Math.min(totalVotes, 4)));
  }, [totalVotes, numTeams]);

  const tie = useMemo(() => detectVoteTie(tallies), [tallies]);

  const winner = useMemo(() => {
    if (tallies.length === 0) return null;
    // Manuelle Moderator-Wahl (Gleichstand) hat Vorrang vor der Auto-Sortierung.
    const topId =
      chosenId ?? [...tallies].sort((a, b) => b.count - a.count)[0]?.topicId;
    const topic = state.workshop.topics.find((t) => t.id === topId);
    const count = tallies.find((t) => t.topicId === topId)?.count ?? 0;
    return topic ? { topic, count } : null;
  }, [tallies, state.workshop.topics, chosenId]);

  const pickWinner = async (topicId: string) => {
    setBusy(true);
    setError(null);
    try {
      await api.winner.set(state.auth.token, topicId);
      setChosenId(topicId);
    } catch (err) {
      console.error(err);
      setError("Konnte den Sieger nicht setzen.");
    } finally {
      setBusy(false);
    }
  };

  const askAssign = () => {
    if (numTeams === null || busy) return;
    setConfirmOpen(true);
  };

  const doAssign = async () => {
    if (numTeams === null) return;
    // Set busy synchronously so a fast double-click on the confirm button
    // can't slip a second request through before React re-renders.
    setBusy(true);
    setError(null);
    setConfirmOpen(false);
    try {
      await api.admin.assignTeams(state.auth.token, numTeams);
      const p = await api.phase.get(state.auth.token);
      dispatch({ type: "phase/set", phase: p });
    } catch (err) {
      console.error(err);
      setError("Konnte Teams nicht verteilen.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="winner-root">
      <div className="deck-bg" aria-hidden="true" />
      <Confetti count={100} />

      <header className="winner-head rise rise-1">
        <div className="deck-eyebrow" style={{ color: "var(--svo-blau)" }}>
          Workshop · Ergebnis
        </div>
        <h1 className="deck-title">
          Das <span className="accent">Sieger-Thema</span> ist
        </h1>
      </header>

      <div className="winner-stage">
        <div className="winner-trophy rise rise-2" aria-hidden="true">
          🏆
        </div>
        {winner ? (
          <div className="winner-title rise rise-3">{winner.topic.title}</div>
        ) : (
          <div className="winner-title rise rise-3">—</div>
        )}
        <div className="winner-meta rise rise-4">
          <span>
            <b>{winner?.count ?? 0}</b> Stimmen
          </span>
          <span className="dot">·</span>
          <span>
            von <b>{totalVotes}</b> Teilnehmern
          </span>
        </div>
      </div>

      {tie.isTie && !readOnly && (
        <div className="winner-tie rise rise-5">
          <div className="winner-cta-label">
            Gleichstand bei <b>{tie.maxCount}</b> Stimmen — bitte Sieger wählen:
          </div>
          <div className="winner-cta-row">
            {tie.tiedTopicIds.map((id) => {
              const topic = state.workshop.topics.find((t) => t.id === id);
              if (!topic) return null;
              return (
                <button
                  key={id}
                  type="button"
                  className={`winner-tie-option${chosenId === id ? " active" : ""}`}
                  onClick={() => pickWinner(id)}
                  disabled={busy}
                >
                  {topic.title}
                </button>
              );
            })}
          </div>
          <div className="winner-cta-hint">
            Ohne Auswahl gewinnt automatisch das zuerst gelistete Thema.
          </div>
        </div>
      )}

      <div className="winner-cta rise rise-5">
        <div className="winner-cta-label">Wie viele Teams sollen es werden?</div>
        <div className="winner-cta-row">
          {[1, 2, 3, 4, 5, 6].map((n) => (
            <button
              key={n}
              type="button"
              className={`winner-team-preset${numTeams === n ? " active" : ""}`}
              onClick={() => setNumTeams(n)}
              disabled={busy}
            >
              {n}
            </button>
          ))}
        </div>
        {error && <div className="winner-error">{error}</div>}
        <button
          type="button"
          className={`btn btn-success winner-cta-btn${readOnly ? " is-readonly" : ""}`}
          onClick={askAssign}
          disabled={busy || totalVotes === 0 || numTeams === null || readOnly}
          aria-busy={busy}
          title={readOnly ? READ_ONLY_TITLE : undefined}
        >
          {busy
            ? "…"
            : `→ ${totalVotes} Teilnehmer auf ${numTeams ?? "?"} Team${(numTeams ?? 1) === 1 ? "" : "s"} verteilen`}
        </button>
        <div className="winner-cta-hint">
          Jedes Handy bekommt direkt seine Team-Nummer angezeigt.
        </div>
      </div>

      <ConfirmDialog
        open={confirmOpen}
        title="Teams jetzt verteilen?"
        message={`${totalVotes} Teilnehmer werden zufällig auf ${numTeams ?? "?"} Team${(numTeams ?? 1) === 1 ? "" : "s"} aufgeteilt.\n\nDiese Aktion kann nicht rückgängig gemacht werden.`}
        confirmLabel="Ja, verteilen"
        cancelLabel="Abbrechen"
        variant="primary"
        onConfirm={doAssign}
        onCancel={() => setConfirmOpen(false)}
        busy={busy}
      />
    </div>
  );
}
